import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { basePathSite } from '@/pages/assistente-evento/utils';
import LinksDivulgar from '../components/LinksDivulgar';
import type { LinkDivulgar } from '../types';

interface Props {
  eventoId: number;
  eventoNome: string;
  slug: string;
  mostrarLoja: boolean;
  vendeIngressos: boolean;
  aoFechar: () => void;
}

interface SituacaoModalidade {
  id: number;
  nome: string;
  publica: boolean;
  ordem: number;
  situacao: string;
  inscricoes_abrem_em: string | null;
  valor_situacao: string;
  menor_valor_centavos: number | null;
}

export default function DivulgarModal({
  eventoId,
  eventoNome,
  slug,
  mostrarLoja,
  vendeIngressos,
  aoFechar,
}: Props) {
  const [copiado, setCopiado] = useState<string | null>(null);
  const [links, setLinks] = useState<LinkDivulgar[]>([]);

  useEffect(() => {
    let ativo = true;
    async function carregar() {
      try {
        const { data, error } = await supabase
          .from('vw_situacao_modalidade')
          .select(
            'id, nome, publica, ordem, situacao, inscricoes_abrem_em, valor_situacao, menor_valor_centavos',
          )
          .eq('evento_id', eventoId);
        if (error) return;
        const situacoes = (data as SituacaoModalidade[]) || [];
        const preliminares: LinkDivulgar[] = [...situacoes]
          .sort((a, b) => a.ordem - b.ordem)
          .map((s) => ({
            id: s.id,
            nome: s.nome,
            valor_situacao: s.valor_situacao,
            valor_centavos: s.menor_valor_centavos,
            publica: s.publica,
            situacao: s.situacao,
            inscricoes_abrem_em: s.inscricoes_abrem_em,
            codigo: null,
          }));

        // Para participações privadas, busca o código pela função protegida.
        const codigos = new Map<number, string>();
        await Promise.all(
          preliminares
            .filter((l) => !l.publica)
            .map(async (l) => {
              const { data: codigo } = await supabase.rpc('codigo_da_modalidade', {
                p_modalidade_id: l.id,
              });
              codigos.set(l.id, (codigo as string) || '');
            }),
        );
        if (!ativo) return;
        setLinks(
          preliminares.map((l) => ({
            ...l,
            codigo: l.publica ? null : codigos.get(l.id) ?? '',
          })),
        );
      } catch {
        /* silencioso: sem links de participação */
      }
    }
    carregar();
    return () => {
      ativo = false;
    };
  }, [eventoId]);

  const urlEvento = `${window.location.origin}${basePathSite()}/e/${slug}`;
  const urlFichas = `${window.location.origin}${basePathSite()}/e/${slug}/fichas`;

  async function copiar(chave: string, url: string) {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      /* silencioso */
    }
    setCopiado(chave);
    setTimeout(() => setCopiado((c) => (c === chave ? null : c)), 2000);
  }

  function enviarWhatsApp(url: string) {
    const texto = encodeURIComponent(`${eventoNome} — veja e participe: ${url}`);
    window.open(`https://wa.me/?text=${texto}`, '_blank', 'noopener,noreferrer');
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={aoFechar} aria-hidden />
      <div className="relative z-10 flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl bg-white shadow-xl sm:rounded-3xl">
        <div className="flex items-center justify-between gap-3 border-b border-secondary-100 px-5 py-4">
          <h2 className="text-lg font-bold text-foreground-950">Divulgar</h2>
          <button
            type="button"
            onClick={aoFechar}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-100"
            aria-label="Fechar"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 pb-6">
          <div className="divide-y divide-secondary-100">
            <LinhaLink
              rotulo="Link do evento"
              url={urlEvento}
              copiado={copiado === 'evento'}
              aoCopiar={() => copiar('evento', urlEvento)}
              aoWhatsApp={() => enviarWhatsApp(urlEvento)}
            />

            {mostrarLoja && (
              <LinhaLink
                rotulo="Link das fichas"
                url={urlFichas}
                copiado={copiado === 'fichas'}
                aoCopiar={() => copiar('fichas', urlFichas)}
                aoWhatsApp={() => enviarWhatsApp(urlFichas)}
              />
            )}

            {mostrarLoja && (
              <div className="flex items-center justify-between gap-3 py-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground-900">Cartaz das fichas</p>
                  <p className="truncate text-xs text-secondary-500">
                    Página A4 pronta para imprimir com o QR.
                  </p>
                </div>
                <Link
                  to={`/cartaz-fichas/${eventoId}`}
                  onClick={aoFechar}
                  className="flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-secondary-300 bg-white px-3 py-1.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-printer-line" />
                  </span>
                  Cartaz
                </Link>
              </div>
            )}
          </div>

          {links.length > 0 && (
            <div className="mt-6">
              <LinksDivulgar
                eventoNome={eventoNome}
                slug={slug}
                links={links}
                vendeIngressos={vendeIngressos}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LinhaLink({
  rotulo,
  url,
  copiado,
  aoCopiar,
  aoWhatsApp,
}: {
  rotulo: string;
  url: string;
  copiado: boolean;
  aoCopiar: () => void;
  aoWhatsApp: () => void;
}) {
  return (
    <div className="py-4">
      <p className="text-sm font-medium text-foreground-900">{rotulo}</p>
      <code className="mt-1 block min-w-0 truncate font-mono text-xs text-foreground-800">
        {url.replace(/^https?:\/\//, '')}
      </code>
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          onClick={aoWhatsApp}
          className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-secondary-300 bg-white px-3 py-1.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
        >
          <span className="flex h-4 w-4 items-center justify-center text-green-600">
            <i className="ri-whatsapp-line" />
          </span>
          WhatsApp
        </button>
        <button
          type="button"
          onClick={aoCopiar}
          className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-600"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className={copiado ? 'ri-check-line' : 'ri-file-copy-line'} />
          </span>
          {copiado ? 'Copiado ✓' : 'Copiar'}
        </button>
      </div>
    </div>
  );
}